import React, { useMemo, useState } from "react";
import {
  Navbar,
  Nav,
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Badge,
  Accordion,
  Modal,
  ProgressBar,
  Carousel,
  Alert,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  Check2Circle,
  ShieldLock,
  GraphUpArrow,
  PersonBadge,
  Facebook,
  Twitter,
  Linkedin,
  StarFill,
  QuestionCircle,
} from "react-bootstrap-icons";

// -----------------------------
// MortgageReadyScoreCard.com - Landing Page
// Tech: React + TypeScript + React-Bootstrap + react-bootstrap-icons
// -----------------------------

// ---- Types ----
interface LeadForm {
  fullName: string;
  email: string;
  state: string;
  estCreditScore: number | "";
  monthlyDebt: number | "";
  homePrice: number | "";
  downPayment: number | "";
  consent: boolean;
}

interface ScoreResult {
  score: number; // 0 - 100
  grade: "A" | "B" | "C" | "D" | "F";
  headline: string;
  suggestions: string[];
}

const statesUS = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

// ---- Helpers ----
function clamp(n: number, min: number, max: number) { return Math.max(min, Math.min(max, n)); }

function computeScore(input: LeadForm): ScoreResult {
  const credit = typeof input.estCreditScore === "number" ? clamp(input.estCreditScore, 300, 850) : 600;
  const price = typeof input.homePrice === "number" ? Math.max(0, input.homePrice) : 0;
  const down = typeof input.downPayment === "number" ? Math.max(0, input.downPayment) : 0;
  const debt = typeof input.monthlyDebt === "number" ? Math.max(0, input.monthlyDebt) : 0;

  // Basic, transparent scoring model for demo purposes
  // Factors: credit (50%), LTV (30%), DTI proxy (20%)
  const creditComponent = ((credit - 300) / (850 - 300)) * 50; // 0..50
  const ltv = price > 0 ? 1 - down / price : 1; // higher LTV worse
  const ltvComponent = (1 - clamp(1 - ltv, 0, 1)) * 30; // 0..30 (more down payment -> lower ltv -> better score)
  const dtiProxy = price > 0 ? clamp(debt / (price / 200), 0, 1) : 1; // crude proxy
  const dtiComponent = (1 - dtiProxy) * 20; // 0..20

  const raw = creditComponent + ltvComponent + dtiComponent; // 0..100
  const score = Math.round(clamp(raw, 0, 100));

  const grade: ScoreResult["grade"] = score >= 90 ? "A" : score >= 75 ? "B" : score >= 60 ? "C" : score >= 45 ? "D" : "F";

  const headlineMap: Record<ScoreResult["grade"], string> = {
    A: "You’re Mortgage‑Ready!",
    B: "You’re Close—Great Shape",
    C: "Almost There—Tighten a Few Things",
    D: "Make a Plan—You’re Getting There",
    F: "Start Here—Quick Wins First",
  };

  const suggestionsBase: string[] = [];
  if (credit < 680) suggestionsBase.push("Dispute errors and pay down revolving balances to lift credit into 680+ range.");
  if (ltv > 0.9) suggestionsBase.push("Increase your down payment or explore down‑payment assistance to reduce LTV.");
  if (dtiProxy > 0.43) suggestionsBase.push("Lower monthly debt or raise income to bring DTI below ~43%.");
  if (suggestionsBase.length === 0) suggestionsBase.push("Lock your rate quotes with 3 lenders and compare total cost (not just APR).");

  return {
    score,
    grade,
    headline: headlineMap[grade],
    suggestions: suggestionsBase,
  };
}

// ---- Main Component ----
const HeroBadge: React.FC<{ text: string }> = ({ text }) => (
  <Badge bg="success" pill className="ms-2 align-middle">
    {text}
  </Badge>
);

const Feature: React.FC<{ icon: React.ReactNode; title: string; text: string }>
  = ({ icon, title, text }) => (
  <Card className="h-100 shadow-sm border-0">
    <Card.Body>
      <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
        <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
          {icon}
        </div>
        <Card.Title className="mb-0 fw-semibold">{title}</Card.Title>
      </div>
      <Card.Text className="text-secondary">{text}</Card.Text>
    </Card.Body>
  </Card>
);

const TestimonialCard: React.FC<{ name: string; quote: string; stars?: number }>
  = ({ name, quote, stars = 5 }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body>
      <div className="mb-2" aria-label={`${stars} star rating`}>
        {Array.from({ length: stars }).map((_, i) => <StarFill key={i} className="me-1" />)}
      </div>
      <blockquote className="blockquote mb-0">
        <p className="fs-6">“{quote}”</p>
        <footer className="blockquote-footer mt-2">{name}</footer>
      </blockquote>
    </Card.Body>
  </Card>
);

const FooterLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} className="text-decoration-none text-secondary">{children}</a>
);

const PlaceholderImg: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`bg-light rounded-3 w-100 ${className ?? ""}`} style={{ aspectRatio: "16 / 9" }} />
);

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const MortgageReadyScoreCardLandingPage_2025_11_06: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lead, setLead] = useState<LeadForm>({
    fullName: "",
    email: "",
    state: "CO",
    estCreditScore: "",
    monthlyDebt: "",
    homePrice: "",
    downPayment: "",
    consent: false,
  });

  const result = useMemo(() => computeScore(lead), [lead]);
  const progressVariant = result.score >= 75 ? "success" : result.score >= 60 ? "info" : result.score >= 45 ? "warning" : "danger";

  // Separate handlers to satisfy react-bootstrap types
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.currentTarget;
    setLead((prev) => ({
      ...prev,
      [name]: type === "checkbox"
        ? checked
        : ["estCreditScore", "monthlyDebt", "homePrice", "downPayment"].includes(name)
          ? (value === "" ? "" : Number(value))
          : value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.currentTarget;
    setLead((prev) => ({ ...prev, [name]: value }));
  };

  const formIsValid =
    lead.fullName.trim().length > 1 &&
    /.+@.+\..+/.test(lead.email) &&
    !!lead.state &&
    lead.consent;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!formIsValid) return;

    // In production: POST to API Gateway or your backend.
    // fetch("/api/leads", { method: "POST", body: JSON.stringify(lead) })

    setShowModal(true);
  };

  return (
    <div className="bg-white">
      {/* Top Nav */}
      <Navbar expand="lg" sticky="top" bg="white" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Mortgage<strong className="text-primary">Ready</strong>ScoreCard<small className="text-secondary">.com</small>
            <HeroBadge text="Free" />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="main-nav" />
          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto" navbarScroll>
              <Nav.Link onClick={() => scrollToId("how")}>How it works</Nav.Link>
              <Nav.Link onClick={() => scrollToId("features")}>Features</Nav.Link>
              <Nav.Link onClick={() => scrollToId("pricing")}>Pricing</Nav.Link>
              <Nav.Link onClick={() => scrollToId("faqs")}>FAQs</Nav.Link>
              <Nav.Link onClick={() => scrollToId("get-started")}>Get Started</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero */}
      <section className="py-5 py-lg-6 bg-light" id="hero">
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={6}>
              <h1 className="display-5 fw-bold">
                Know your <span className="text-primary">Mortgage‑Ready</span> score in 60 seconds.
              </h1>
              <p className="lead text-secondary mt-3">
                A simple, transparent score that shows where you stand today—and the exact steps to improve fast.
              </p>
              <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
                <Button size="lg" onClick={() => scrollToId("get-started")}>
                  Get My Free Score
                </Button>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>We never sell your data. Opt‑out anytime.</Tooltip>}
                >
                  <div className="d-flex align-items-center text-secondary">
                    <ShieldLock className="me-2" /> Privacy‑first
                  </div>
                </OverlayTrigger>
              </div>
              <div className="mt-4 d-flex align-items-center text-secondary" style={{ gap: 12 }}>
                <Check2Circle className="text-success" /> Soft‑check inputs only (no credit pull)
              </div>
            </Col>
            <Col lg={6}>
              <Card className="shadow border-0">
                <Card.Body>
                  <div className="d-flex align-items-center mb-2">
                    <GraphUpArrow className="me-2" />
                    <h5 className="mb-0">Preview: Your Score</h5>
                    <Badge bg={progressVariant} className="ms-2">{result.grade}</Badge>
                  </div>
                  <ProgressBar now={result.score} variant={progressVariant} className="mb-2" />
                  <div className="small text-secondary">Live estimate updates as you type.</div>
                  <hr />
                  <Form noValidate onSubmit={handleSubmit} id="get-started">
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="fullName">
                          <Form.Label>Full name</Form.Label>
                          <Form.Control
                            name="fullName"
                            value={lead.fullName}
                            onChange={handleInputChange}
                            placeholder="Alex Homebuyer"
                            isInvalid={submitted && lead.fullName.trim().length < 2}
                            required
                          />
                          <Form.Control.Feedback type="invalid">Please enter your name.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            name="email"
                            type="email"
                            value={lead.email}
                            onChange={handleInputChange}
                            placeholder="alex@example.com"
                            isInvalid={submitted && !/.+@.+\..+/.test(lead.email)}
                            required
                          />
                          <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="state">
                          <Form.Label>State</Form.Label>
                          <Form.Select name="state" value={lead.state} onChange={handleSelectChange} required>
                            {statesUS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="estCreditScore">
                          <Form.Label>Est. credit score</Form.Label>
                          <Form.Control name="estCreditScore" type="number" min={300} max={850} value={lead.estCreditScore} onChange={handleInputChange} placeholder="720" />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group controlId="monthlyDebt">
                          <Form.Label>Monthly debt ($)</Form.Label>
                          <Form.Control name="monthlyDebt" type="number" min={0} value={lead.monthlyDebt} onChange={handleInputChange} placeholder="450" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="homePrice">
                          <Form.Label>Target home price ($)</Form.Label>
                          <Form.Control name="homePrice" type="number" min={0} value={lead.homePrice} onChange={handleInputChange} placeholder="450000" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="downPayment">
                          <Form.Label>Down payment ($)</Form.Label>
                          <Form.Control name="downPayment" type="number" min={0} value={lead.downPayment} onChange={handleInputChange} placeholder="45000" />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Check
                          id="consent"
                          name="consent"
                          checked={lead.consent}
                          onChange={handleInputChange}
                          label={<span>I agree to the <a href="#">Terms</a> and <a href="#">Privacy</a>.</span>}
                          required
                          isInvalid={submitted && !lead.consent}
                        />
                      </Col>
                      <Col xs={12} className="d-grid d-md-flex gap-3">
                        <Button type="submit" size="lg" className="px-4">Calculate My Score</Button>
                        <Button variant="outline-secondary" size="lg" onClick={() => scrollToId("how")}>
                          How it works
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Social proof */}
      <section className="py-4">
        <Container>
          <Row className="text-center text-secondary small g-4 align-items-center justify-content-center">
            <Col xs="auto">Trusted by home buyers and agents across the U.S.</Col>
            <Col xs="auto"><PersonBadge className="me-1" /> No spam. No hard credit pull.</Col>
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section className="py-5" id="features">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">Everything you need to get mortgage‑ready</h2>
              <p className="text-secondary mb-0">Immediate score, personalized checklist, and practical next steps tailored to your numbers.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}><Feature icon={<GraphUpArrow />} title="Instant Score" text="See your Mortgage‑Ready score in seconds—no credit pull required." /></Col>
            <Col md={6} lg={4}><Feature icon={<ShieldLock />} title="Privacy‑First" text="Your data stays yours. Export or delete with one click." /></Col>
            <Col md={6} lg={4}><Feature icon={<Check2Circle />} title="Actionable Steps" text="Get a step‑by‑step checklist to improve your approval odds." /></Col>
          </Row>
        </Container>
      </section>

      {/* How it works */}
      <section className="py-5 bg-light" id="how">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">How it works</h2>
              <p className="text-secondary">Three quick steps to clarity.</p>
            </Col>
          </Row>
          <Row className="g-4 align-items-stretch">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="primary">Step 1</Badge>
                  <Card.Title className="mt-2">Enter a few details</Card.Title>
                  <Card.Text className="text-secondary">Credit estimate, home price, down payment and monthly debts. No SSN. No hard pull.</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="primary">Step 2</Badge>
                  <Card.Title className="mt-2">Get your score</Card.Title>
                  <Card.Text className="text-secondary">We calculate a transparent, 0–100 score and a simple grade—A to F—based on your inputs.</Card.Text>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="primary">Step 3</Badge>
                  <Card.Title className="mt-2">Follow the checklist</Card.Title>
                  <Card.Text className="text-secondary">Actionable, prioritized steps to boost your approval odds and lower costs.</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials / Carousel */}
      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">Real people. Real results.</h2>
              <p className="text-secondary">A few words from early users.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}><TestimonialCard name="Maya P." quote="I thought I was months away—turns out I just needed to tweak my down payment. Closed in 45 days!" /></Col>
            <Col md={6} lg={4}><TestimonialCard name="Chris D." quote="The checklist was gold. I raised my score into ‘A’ territory in two weeks." /></Col>
            <Col md={6} lg={4}><TestimonialCard name="Jorge L." quote="Loved the privacy‑first approach. No spam, just my plan." /></Col>
          </Row>
          <Row className="mt-4">
            <Col>
              <Carousel className="shadow-sm rounded-3 overflow-hidden">
                <Carousel.Item>
                  <PlaceholderImg />
                  <Carousel.Caption>
                    <h5>From unsure to offer‑ready</h5>
                    <p>Users boost confidence with a clear path to approval.</p>
                  </Carousel.Caption>
                </Carousel.Item>
                <Carousel.Item>
                  <PlaceholderImg />
                  <Carousel.Caption>
                    <h5>Built for buyers & agents</h5>
                    <p>Shareable score helps align everyone, fast.</p>
                  </Carousel.Caption>
                </Carousel.Item>
              </Carousel>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Pricing */}
      <section className="py-5 bg-light" id="pricing">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">Simple pricing</h2>
              <p className="text-secondary">Start free. Upgrade when you want expert help.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold">Free</h5>
                  <p className="text-secondary">Best for first‑time buyers</p>
                  <ul className="list-unstyled small">
                    <li><Check2Circle className="text-success me-2" /> Mortgage‑Ready score</li>
                    <li><Check2Circle className="text-success me-2" /> Personalized checklist</li>
                    <li><Check2Circle className="text-success me-2" /> Export to PDF</li>
                  </ul>
                  <Button className="w-100" onClick={() => scrollToId("get-started")}>Get started</Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm border-primary">
                <Card.Body>
                  <h5 className="fw-bold">Pro</h5>
                  <p className="text-secondary">$9 / month • cancel anytime</p>
                  <ul className="list-unstyled small">
                    <li><Check2Circle className="text-success me-2" /> Advanced scenarios (FHA/VA/Conventional)</li>
                    <li><Check2Circle className="text-success me-2" /> Rate & payment comparisons</li>
                    <li><Check2Circle className="text-success me-2" /> Save multiple profiles</li>
                  </ul>
                  <Button variant="outline-primary" className="w-100" onClick={() => scrollToId("get-started")}>Try Pro</Button>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold">Agent Toolkit</h5>
                  <p className="text-secondary">Contact us for pricing</p>
                  <ul className="list-unstyled small">
                    <li><Check2Circle className="text-success me-2" /> Co‑branded scorecards</li>
                    <li><Check2Circle className="text-success me-2" /> Lead routing & analytics</li>
                    <li><Check2Circle className="text-success me-2" /> Team coaching resources</li>
                  </ul>
                  <Button variant="outline-secondary" className="w-100" onClick={() => scrollToId("contact")}>Talk to sales</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQs */}
      <section className="py-5" id="faqs">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold">Frequently asked questions</h2>
              <p className="text-secondary">Short, helpful answers to common questions.</p>
            </Col>
          </Row>
          <Row>
            <Col lg={10}>
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header><QuestionCircle className="me-2" /> Does this pull my credit?</Accordion.Header>
                  <Accordion.Body>
                    No. We use your self‑reported estimate. There’s no hard inquiry and no impact to your score.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                  <Accordion.Header><QuestionCircle className="me-2" /> Is my data private?</Accordion.Header>
                  <Accordion.Body>
                    Yes. We never sell personal data. You can export or delete your inputs at any time.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                  <Accordion.Header><QuestionCircle className="me-2" /> What loan types are supported?</Accordion.Header>
                  <Accordion.Body>
                    The free score uses a universal model. Pro unlocks FHA, VA, and Conventional nuances for more precise guidance.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact / Callout */}
      <section className="py-5 bg-primary text-white" id="contact">
        <Container>
          <Row className="align-items-center g-4">
            <Col lg={8}>
              <h2 className="fw-bold">Questions or partnerships?</h2>
              <p className="mb-0">We’d love to chat about agents, lenders, or affiliate opportunities.</p>
            </Col>
            <Col lg={4} className="d-grid d-lg-flex justify-content-lg-end gap-3">
              <Button variant="light" onClick={() => scrollToId("get-started")}>Calculate my score</Button>
              <Button variant="outline-light" onClick={() => window.location.assign("mailto:hello@MortgageReadyScoreCard.com")}>Email us</Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 border-top" id="footer">
        <Container>
          <Row className="align-items-center gy-3">
            <Col md={6}>
              <div className="fw-bold">MortgageReadyScoreCard.com</div>
              <div className="text-secondary small">© {new Date().getFullYear()} — All rights reserved.</div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end gap-4">
              <FooterLink href="#">Privacy</FooterLink>
              <FooterLink href="#">Terms</FooterLink>
              <FooterLink href="#">Support</FooterLink>
              <a href="#" aria-label="Twitter" className="text-secondary"><Twitter /></a>
              <a href="#" aria-label="LinkedIn" className="text-secondary"><Linkedin /></a>
              <a href="#" aria-label="Facebook" className="text-secondary"><Facebook /></a>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Result Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Your Mortgage‑Ready Score</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant={progressVariant as any} className="d-flex align-items-center justify-content-between">
            <div>
              <div className="fw-bold fs-5">{result.headline}</div>
              <div className="small text-secondary">Score</div>
            </div>
            <div className="display-6 fw-bold mb-0">{result.score}</div>
          </Alert>
          <ul className="small mb-0">
            {result.suggestions.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          <Button onClick={() => {
            setShowModal(false);
            window.setTimeout(() => scrollToId("pricing"), 100);
          }}>See pricing</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MortgageReadyScoreCardLandingPage_2025_11_06;
